import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { getDaybookList, ResponseGetDaybookList } from './api/getDaybookList';
import { useParams } from 'react-router-dom';

import { useInView } from 'react-intersection-observer';
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import i18n from '@/localisation/i18n';
import { FadeReplace } from '@/UI/animations/FadeReplace';
import { HeaderInfo } from '@/UI/HeaderInfo';
import {
  daybookMarkAllViewed,
  SubmitDaybookMarkAllViewed,
} from './api/smerMarkAllViewed';
import { useUnread } from '@/hooks/useUnread';
import { aggregateDataByDate } from '../Body/helper';
import { ItemDaybook } from './components/ItemDaybook';
import { useClient } from '@/hooks/useClient';
import clsx from 'clsx';

type FilterType = 'all' | 'standard' | 'dream' | 'gratitude';

export const Daybook = () => {
  const currentClient = useClient();
  const { client } = useParams();
  const { isDaybookUnread } = useUnread();
  const queryClient = useQueryClient();
  const [selectedFilter, setSelectedFilter] = useState<FilterType>('all');

  const storageKey = `daybook_scroll_${client}`;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    refetch,
  } = useInfiniteQuery<ResponseGetDaybookList>({
    queryKey: ['daybookList', client],

    queryFn: ({ pageParam }) =>
      getDaybookList({ user: client!, offset: pageParam as number }),
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) {
        return undefined;
      }

      return lastPage.length < 20 ? undefined : allPages.length * 20;
    },
  });

  useEffect(() => {
    const savedPosition = sessionStorage.getItem(storageKey);
    const scrollElement = document.querySelector('main');
    if (savedPosition && scrollElement) {
      scrollElement.scrollTop = Number(savedPosition);
    }
  }, [storageKey]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollElement = document.querySelector('main');
      if (scrollElement) {
        sessionStorage.setItem(storageKey, scrollElement.scrollTop.toString());
      }
    };

    const scrollElement = document.querySelector('main');
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [storageKey]);

  // Хук для отслеживания видимости элемента
  const { ref, inView } = useInView({
    threshold: 1.0,
    triggerOnce: false,
  });

  const { mutate } = useMutation({
    mutationFn: (data: SubmitDaybookMarkAllViewed) =>
      daybookMarkAllViewed(data),
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  // Подсчет количества записей по типам (исключая 'question')
  const pages = data?.pages;
  const counts = useMemo(() => {
    if (!pages?.length || !pages[0]) {
      return { all: 0, standard: 0, dream: 0, gratitude: 0 };
    }
    const list = pages.reduce((acc, page) => acc.concat(page), []) || [];
    const valid = list.filter(item => item.typeDaybook !== 'question');

    return {
      all: valid.length,
      standard: valid.filter(
        item => !item.typeDaybook || item.typeDaybook === 'standard'
      ).length,
      dream: valid.filter(item => item.typeDaybook === 'dream').length,
      gratitude: valid.filter(item => item.typeDaybook === 'gratitude').length,
    };
  }, [pages]);

  const aggregation = useMemo(() => {
    if (!pages?.length || !pages[0]) return [];

    const list = pages.reduce((acc, page) => acc.concat(page), []) || [];
    // Исключаем question из отображения
    const validList = list.filter(item => item.typeDaybook !== 'question');

    const filtered = validList.filter(item => {
      if (selectedFilter === 'all') return true;
      if (selectedFilter === 'standard') {
        return !item.typeDaybook || item.typeDaybook === 'standard';
      }
      return item.typeDaybook === selectedFilter;
    });

    return aggregateDataByDate(filtered);
  }, [pages, selectedFilter]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
    >
      <HeaderInfo
        title={i18n.t('client.tab.daybook.title')}
        subtitle={currentClient?.userName ?? ''}
        backUrl={`/${client}`}
        actionButton={
          <button
            disabled={!isDaybookUnread || counts.all === 0}
            onClick={() => mutate({ user: client! })}
            className="btn btn-primary w-full text-sm font-bold"
          >
            <svg
              width="16"
              height="16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.6 13.456A5.97 5.97 0 0 1 12 12.4c.568 0 1.117.08 1.636.226a.6.6 0 0 0 .764-.577v-8.8a.6.6 0 0 0-.437-.578A7.205 7.205 0 0 0 12 2.401a7.17 7.17 0 0 0-3.4.851v10.204ZM7.4 3.252A7.17 7.17 0 0 0 4 2.4c-.68 0-1.339.095-1.964.271a.6.6 0 0 0-.436.578v8.8a.6.6 0 0 0 .763.577A6.003 6.003 0 0 1 4 12.4a5.97 5.97 0 0 1 3.4 1.056V3.252Z"
                fill="currentColor"
              />
            </svg>
            {i18n.t('mark.all.read')}
          </button>
        }
      >
        {/* Фильтры по типам записей */}
        {counts.all > 0 && (
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button
              onClick={() => setSelectedFilter('all')}
              className={clsx(
                'flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all duration-200 cursor-pointer',
                selectedFilter === 'all'
                  ? 'border-primary bg-primary/20 text-white'
                  : 'border-white/10 bg-card text-additional hover:border-white/20 hover:text-white'
              )}
            >
              <span>{i18n.t('daybook.filter.all')}</span>
              <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px]">
                {counts.all}
              </span>
            </button>

            {counts.standard > 0 && (
              <button
                onClick={() => setSelectedFilter('standard')}
                className={clsx(
                  'flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all duration-200 cursor-pointer',
                  selectedFilter === 'standard'
                    ? 'border-teal-500/40 bg-teal-500/15 text-teal-200'
                    : 'border-white/10 bg-card text-additional hover:border-teal-500/30 hover:text-teal-200'
                )}
              >
                <svg
                  className="h-3 w-3 text-teal-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5Z" />
                </svg>
                <span>{i18n.t('daybook.filter.standard')}</span>
                <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px]">
                  {counts.standard}
                </span>
              </button>
            )}

            {counts.dream > 0 && (
              <button
                onClick={() => setSelectedFilter('dream')}
                className={clsx(
                  'flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all duration-200 cursor-pointer',
                  selectedFilter === 'dream'
                    ? 'border-indigo-500/40 bg-indigo-500/15 text-indigo-200'
                    : 'border-white/10 bg-card text-additional hover:border-indigo-500/30 hover:text-indigo-200'
                )}
              >
                <svg
                  className="h-3 w-3 text-indigo-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" fill="currentColor" fillOpacity="0.3" />
                </svg>
                <span>{i18n.t('daybook.filter.dream')}</span>
                <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px]">
                  {counts.dream}
                </span>
              </button>
            )}

            {counts.gratitude > 0 && (
              <button
                onClick={() => setSelectedFilter('gratitude')}
                className={clsx(
                  'flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all duration-200 cursor-pointer',
                  selectedFilter === 'gratitude'
                    ? 'border-rose-500/40 bg-rose-500/15 text-rose-200'
                    : 'border-white/10 bg-card text-additional hover:border-rose-500/30 hover:text-rose-200'
                )}
              >
                <svg
                  className="h-3 w-3 text-rose-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"
                    fill="currentColor"
                    fillOpacity="0.3"
                  />
                </svg>
                <span>{i18n.t('daybook.filter.gratitude')}</span>
                <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px]">
                  {counts.gratitude}
                </span>
              </button>
            )}
          </div>
        )}

        <div className="mt-6">
          {aggregation?.length === 0 && !isFetching ? (
            <div className="flex h-32 items-center justify-center text-center text-additional">
              {i18n.t('no.records')}
            </div>
          ) : (
            <FadeReplace
              firstElement={
                <div className="mt-3 grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
                  <div className="skeleton h-44 w-full rounded-lg"></div>
                  <div className="skeleton h-44 w-full rounded-lg"></div>
                  <div className="skeleton h-44 w-full rounded-lg"></div>
                  <div className="skeleton h-44 w-full rounded-lg"></div>
                  <div className="skeleton h-44 w-full rounded-lg"></div>
                  <div className="skeleton h-44 w-full rounded-lg"></div>
                </div>
              }
              secondElement={
                <div>
                  {aggregation.map(item => (
                    <div key={item.date} className="mt-8 first:mt-2">
                      <h4 className="text-base font-semibold text-white/90">
                        {item.date}
                      </h4>
                      <div className="mt-3 grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
                        {item.list.map(daybook => (
                          <ItemDaybook
                            key={daybook.id}
                            client={client!}
                            {...daybook}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              }
              showFirst={
                aggregation.length === 0 || (isFetching && !isFetchingNextPage)
              }
            />
          )}
        </div>
        <div ref={ref} className="loading-indicator">
          {isFetchingNextPage && data && (
            <div className="mt-4 flex justify-center">
              <span className="loading loading-ring loading-lg"></span>
            </div>
          )}
        </div>
      </HeaderInfo>
    </motion.div>
  );
};
